import { defineStore, storeToRefs } from 'pinia';
import { PlaceAutocompleteModelDto, PlaceType } from '@/types/webapi';
import { IPlaceService, IPlaceServiceId } from '@/services/IPlaceService';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useContainer } from '@/plugins/inversify';
import { SearchMode, useMainStore } from '@/features/common/stores';
import { isAxiosError } from 'axios';
import { isEmpty, isEqual } from 'lodash-es';
import { useMapStore } from '@/features/map/stores';

export const usePlacesStore = defineStore('placesStore', () => {
    const mainStore = useMainStore();
    const mapStore = useMapStore();
    const { searchMode } = storeToRefs(mainStore);
    const container = useContainer();
    const placeService = container.get<IPlaceService>(IPlaceServiceId);

    const loadingStates = reactive({
        from: false,
        to: false
    });
    const fromPlace = ref<(PlaceAutocompleteModelDto & { isMyLocation?: boolean }) | null>(null);
    const toPlace = ref<(PlaceAutocompleteModelDto & { isMyLocation?: boolean }) | null>(null);
    const fromPlaceIsValid = computed(
        () => !!(fromPlace.value?.id && fromPlace.value?.type !== PlaceType.Unknown && !isEmpty(fromPlace.value.coordinates))
    );
    const toPlaceIsValid = computed(
        () => !!(toPlace.value?.id && toPlace.value?.type !== PlaceType.Unknown && !isEmpty(toPlace.value.coordinates))
    );
    const fromIsLoading = computed({
        get() {
            return loadingStates.from;
        },
        set(value: boolean) {
            loadingStates.from = value;
        }
    });
    const toIsLoading = computed({
        get() {
            return loadingStates.to;
        },
        set(value: boolean) {
            loadingStates.to = value;
        }
    });

    const abortControllers = reactive({
        from: new AbortController(),
        to: new AbortController()
    });

    function $reset() {
        fromPlace.value = null;
        toPlace.value = null;

        // reset mapStore
        nextTick(() => mapStore.$reset());
    }

    async function loadPlaceDetails(placeId: string, type: 'from' | 'to') {
        try {
            abortControllers[type] = new AbortController();
            loadingStates[type] = true;
            const details = await placeService.getPlaceDetails(placeId, { signal: abortControllers[type].signal });

            if (type === 'from') {
                fromPlace.value = details;
            } else {
                toPlace.value = details;
            }
        } catch (e) {
            if (isAxiosError(e)) {
                if (e.name === 'CanceledError') return;
            }

            mainStore.registerError(e);
        } finally {
            loadingStates[type] = false;
        }
    }

    // fetch place details when places changes
    watch(
        fromPlace,
        async (value, oldValue) => {
            if (!value || isEqual(value, oldValue)) return;

            // abort previous request
            abortControllers.from.abort();

            // load place details if type is PlaceType.Unknown, or coordinates is null
            if (!fromPlaceIsValid.value) {
                await loadPlaceDetails(fromPlace.value!.id, 'from');
            }
        },
        {
            immediate: true,
            deep: true
        }
    );

    watch(
        toPlace,
        async (value, oldValue) => {
            if (!value || isEqual(value, oldValue)) return;

            // abort previous request
            abortControllers.to.abort();

            // load place details if type is PlaceType.Unknown, or coordinates is null
            if (!toPlaceIsValid.value) {
                await loadPlaceDetails(toPlace.value!.id, 'to');
            }
        },
        {
            immediate: true,
            deep: true
        }
    );

    watch(searchMode, value => {
        if (value === SearchMode.Departures) {
            // reset fromPlace if it's not a transit station
            if (fromPlace.value?.type !== PlaceType.TransitStation) {
                fromPlace.value = null;
            }

            // reset toPlace
            toPlace.value = null;
        }
    });

    return {
        fromPlace,
        toPlace,
        fromPlaceIsValid,
        toPlaceIsValid,
        fromIsLoading,
        toIsLoading,
        $reset
    };
});
